import * as React from 'react'
import { Suspense, useEffect, useState } from 'react'
import {
  Switch,
  Route,
  useLocation,
  useHistory,
  RouteComponentProps,
  Redirect,
  matchPath
} from 'react-router-dom'
import 'font-awesome/scss/font-awesome.scss'
import Loader from './components/theme-originals/layout/Loader'
import ScrollToTop from './components/theme-originals/layout/ScrollToTop'
import guestRoutes, { RouteObject } from './routes/guest.routes'
import './assets/scss/style.scss'
import { RoutesFactory } from './routes/logged.routes'
import { LoadingOverlay } from './components/atoms/LoadingOverlay'
import { URL_API } from './services/api_url'
import { RemoteGetUser } from './data/usecases/User/RemoteGetUser'
import { AxiosHttpClient } from './infra/http/axiosHttpClient'
import { LoggedRoutesContainer } from './components/atoms/LoggedRoutesContainer'
import { UserType } from './domain/models/UserModel'
import { UserContext, ContextSummary } from './interfaces/UserContext'

export interface User {
  token: string
  name: string
  email?: string
  fullName?: string
  type?: UserType
  contexts?: ContextSummary[] // Available contexts for this user
  selectedContext?: UserContext // Currently selected context
  selectedContextEncoded?: string // Base64 encoded context for API header
}

interface IAppContext {
  user?: User | null
  setUser?: (user: User) => void
  clearUser?: () => void
  selectContext?: (companyId: string, userOverride?: User) => Promise<any>
  menuVisible?: boolean
  setMenuVisible: (visible: boolean) => void
}

export const AppContext = React.createContext<IAppContext>({
  setMenuVisible: () => console.log('set menu visible')
})
const App = () => {
  const location = useLocation()
  const history = useHistory()

  const storageUser = localStorage.getItem('user')
    ? JSON.parse(localStorage.getItem('user') as string)
    : undefined

  console.log('[APP INIT] Storage user:', storageUser)
  console.log('[APP INIT] Storage user contexts:', storageUser?.contexts)
  console.log(
    '[APP INIT] Storage user contexts length:',
    storageUser?.contexts?.length
  )

  const [user, setUserState] = useState<User | null>(storageUser)
  const [menuVisible, setMenuVisible] = useState<boolean>(true)
  const [routes, setRoutes] = useState<RouteObject[]>([])

  const setUser = (user: User) => {
    const firstAndLastName = (nameArray: string[]) =>
      `${nameArray[0]} ${nameArray[nameArray.length - 1]}`
    localStorage.setItem('user', JSON.stringify(user))
    const axiosHttpClient = new AxiosHttpClient(user.token)
    const URL = URL_API || 'without-url'
    const remoteGetUser = new RemoteGetUser(URL, axiosHttpClient)
    remoteGetUser
      .getMe()
      .then((data) => {
        const newUserState = {
          ...user,
          name: firstAndLastName(data.name.split(' ')),
          fullName: data.name,
          type: data.type,
          // Preserve contexts and selectedContext from the user object
          contexts: user.contexts,
          selectedContext: user.selectedContext,
          selectedContextEncoded: user.selectedContextEncoded
        }
        console.log(
          '[APP setUser] Setting user state with contexts:',
          newUserState.contexts
        )
        console.log(
          '[APP setUser] Contexts length:',
          newUserState.contexts?.length
        )
        setUserState(newUserState)
        setRoutes(RoutesFactory.createRoutes(data as User))
      })
      .catch(() => {
        setUserState(null)
        // Don't bounce visitors off public/guest pages (e.g. password reset).
        // Stale localStorage user + failed getMe() must not hijack these routes.
        const onGuestRoute = guestRoutes.some((route) =>
          matchPath(history.location.pathname, {
            path: route.path,
            exact: route.exact
          })
        )
        if (!onGuestRoute) {
          history.push('/login')
        }
      })
  }

  useEffect(() => {
    if (storageUser) {
      setUser(storageUser)
    }
  }, [])

  // Auto-refresh permissions every 5 minutes
  useEffect(() => {
    if (!user?.selectedContext?.companyId || !user?.token) {
      return
    }

    const refreshPermissions = async () => {
      try {
        const companyId = user.selectedContext?.companyId
        if (!companyId) return

        console.log(
          '[AUTO REFRESH] Refreshing permissions for company:',
          companyId
        )
        const axiosHttpClient = new AxiosHttpClient(user.token)
        const response = await axiosHttpClient.get({
          url: `${URL_API}/auth/context/${companyId}`
        })

        if (response?.context && response?.contextEncoded) {
          const updatedUser = {
            ...user,
            selectedContext: response.context,
            selectedContextEncoded: response.contextEncoded
          }
          console.log(
            '[AUTO REFRESH] Permissions updated, new permission count:',
            response.context.permissions?.length
          )
          localStorage.setItem('user', JSON.stringify(updatedUser))
          setUserState(updatedUser)
        }
      } catch (error) {
        console.error('[AUTO REFRESH] Failed to refresh permissions:', error)
        // Don't disrupt user experience on refresh failure
      }
    }

    // Refresh every 5 minutes (300000ms)
    const interval = setInterval(refreshPermissions, 5 * 60 * 1000)

    console.log(
      '[AUTO REFRESH] Permission auto-refresh enabled (every 5 minutes)'
    )

    return () => {
      clearInterval(interval)
      console.log('[AUTO REFRESH] Permission auto-refresh disabled')
    }
  }, [user?.selectedContext?.companyId, user?.token])

  const clearUser = () => {
    localStorage.removeItem('user')
    setUserState(null)
  }

  // Select a context (company) for the user
  const selectContext = async (companyId: string, userOverride?: User) => {
    console.log('[SELECT CONTEXT] Called with companyId:', companyId)
    const currentUser = userOverride || user
    if (!currentUser) {
      console.log('[SELECT CONTEXT] No user found')
      return
    }

    try {
      const axiosHttpClient = new AxiosHttpClient(currentUser.token)
      console.log(
        '[SELECT CONTEXT] Calling API:',
        `${URL_API}/auth/context/${companyId}`
      )
      const response = await axiosHttpClient.get({
        url: `${URL_API}/auth/context/${companyId}`
      })

      console.log('[SELECT CONTEXT] Response received:', response)

      if (response && response.context && response.contextEncoded) {
        const updatedUser = {
          ...currentUser,
          selectedContext: response.context,
          selectedContextEncoded: response.contextEncoded
        }
        console.log(
          '[SELECT CONTEXT] Updated user with context, selectedContextEncoded:',
          updatedUser.selectedContextEncoded?.substring(0, 20) + '...'
        )
        localStorage.setItem('user', JSON.stringify(updatedUser))

        // Always fetch full user details to ensure name is populated
        console.log('[SELECT CONTEXT] Fetching user details...')
        const URL = URL_API || 'without-url'
        const remoteGetUser = new RemoteGetUser(URL, axiosHttpClient)
        try {
          const userData = await remoteGetUser.getMe()
          const firstAndLastName = (nameArray: string[]) =>
            `${nameArray[0]} ${nameArray[nameArray.length - 1]}`

          const fullUser = {
            ...updatedUser,
            name: firstAndLastName(userData.name.split(' ')),
            fullName: userData.name,
            type: userData.type
          }

          console.log('[SELECT CONTEXT] Full user prepared:', {
            name: fullUser.name,
            fullName: fullUser.fullName,
            hasContext: !!fullUser.selectedContext,
            permissions: fullUser.selectedContext?.permissions?.length
          })

          // Update local storage with full user info
          localStorage.setItem('user', JSON.stringify(fullUser))

          // Set user state
          setUserState(fullUser)

          // Set routes if not already set
          if (routes.length === 0) {
            setRoutes(RoutesFactory.createRoutes(userData as User))
            console.log('[SELECT CONTEXT] Routes set successfully')
          }

          // Return a promise that resolves after state update
          return new Promise((resolve) => {
            setTimeout(() => resolve(fullUser), 100)
          })
        } catch (error) {
          console.error('[SELECT CONTEXT] Failed to fetch user details:', error)
          throw error
        }
      } else {
        console.log('[SELECT CONTEXT] Response missing required fields')
      }
    } catch (error) {
      console.error('[SELECT CONTEXT] Failed to select context:', error)
      throw error
    }
  }

  return (
    <>
      <AppContext.Provider
        value={{
          user,
          setUser,
          clearUser,
          selectContext,
          setMenuVisible,
          menuVisible
        }}
      >
        <ScrollToTop>
          <Suspense fallback={<Loader />}>
            <Route path={guestRoutes.map((x) => x.path)}>
              <Switch location={location} key={location.pathname}>
                {guestRoutes.map((route, index) => {
                  return route.component ? (
                    <Route
                      key={index}
                      path={route.path}
                      exact={route.exact}
                      render={(props: RouteComponentProps) => (
                        <route.component {...props} />
                      )}
                    />
                  ) : (
                    <Redirect to="/" />
                  )
                })}
                {!user && <Redirect to={'/'} />}
              </Switch>
            </Route>
            <Route path={routes.map((x) => x.path)}>
              <LoggedRoutesContainer />
            </Route>
          </Suspense>
        </ScrollToTop>
      </AppContext.Provider>
      <div className="backdrop" />
      <LoadingOverlay />
    </>
  )
}

export default App
